import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Col, Empty, Input, Row, Segmented, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import CountryFlag, { CountryLabel } from '../../../shared/components/CountryFlag';
import { userPath } from '../../../shared/config/paths';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { dedicatedUserApi as api, isOk, errMsg, arr, DC_LABEL, CYCLES, CYCLE_LABEL } from '../../../shared/services/dedicatedService';

const { Text } = Typography;
const money = (p) => formatMoney(Number(p || 0), PLATFORM_CURRENCY);

const gb = (v) => (v == null ? '-' : v >= 1024 ? `${+(v / 1024).toFixed(1)} TB` : `${v} GB`);
const disk = (v) => (v == null ? '-' : v >= 1000 ? `${+(v / 1000).toFixed(1)} TB` : `${v} GB`);
const RAM_STEPS = [32, 64, 128, 256, 512, 1024];

const ModelTable = ({ models, sel, onPick }) => {
    const { t } = useTranslation();
    const [q, setQ] = useState('');
    const [loc, setLoc] = useState([]);
    const [brand, setBrand] = useState('all');
    const [ram, setRam] = useState(0);
    const [type, setType] = useState('all');
    const [unmetered, setUnmetered] = useState(false);
    const hasBw = useMemo(() => models.some((m) => m.bandwidth), [models]);
    const locs = useMemo(() => [...new Set(models.map((m) => m.location || m.data_center))].sort(), [models]);
    const locCountry = useMemo(() => Object.fromEntries(models.filter((m) => m.country).map((m) => [m.location || m.data_center, m.country])), [models]);
    const types = useMemo(() => ['NVMe', 'SSD', 'HDD'].filter((x) => models.some((m) => m.storage_type === x)), [models]);
    const rows = useMemo(() => {
        const k = q.trim().toLowerCase();
        return models.filter((m) => (!k || `${m.name} ${m.cpu} ${m.gpu || ''} ${m.storage}`.toLowerCase().includes(k))
            && (!loc.length || loc.includes(m.location || m.data_center))
            && (brand === 'all' || m.brand === brand)
            && (!ram || (m.ram_gb || 0) >= ram)
            && (type === 'all' || m.storage_type === type)
            && (!unmetered || m.unmetered));
    }, [models, q, loc, brand, ram, type, unmetered]);
    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Search')} value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} />
                <Select mode="multiple" allowClear maxTagCount="responsive" placeholder={t('Location')} value={loc} onChange={setLoc} style={{ minWidth: 180 }}
                    optionFilterProp="text"
                    options={locs.map((l) => ({ value: l, text: t(l), label: <CountryLabel code={locCountry[l]}>{t(l)}</CountryLabel> }))} />
                <Segmented value={brand} onChange={setBrand} options={[{ label: t('All'), value: 'all' }, 'AMD', 'Intel']} />
                <Select value={ram} onChange={setRam} style={{ width: 130 }}
                    options={[{ value: 0, label: t('Any RAM') }, ...RAM_STEPS.map((v) => ({ value: v, label: `≥ ${gb(v)}` }))]} />
                {hasBw && <Checkbox checked={unmetered} onChange={(e) => setUnmetered(e.target.checked)}>{t('Unmetered only')}</Checkbox>}
                {types.length > 1 && (
                    <Select value={type} onChange={setType} style={{ width: 120 }}
                        options={[{ value: 'all', label: t('Any disk') }, ...types.map((x) => ({ value: x, label: x }))]} />
                )}
            </Space>
            <Table size="small" rowKey="id" dataSource={rows} scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
                rowSelection={{ type: 'radio', selectedRowKeys: sel ? [sel] : [], onChange: (_, [m]) => m && onPick(m) }}
                onRow={(m) => ({ onClick: () => onPick(m), style: { cursor: 'pointer' } })}
                columns={[
                    {
                        title: 'CPU', dataIndex: 'cpu', sorter: (a, b) => a.cpu.localeCompare(b.cpu),
                        render: (v, m) => (
                            <Space direction="vertical" size={0}>
                                <Text strong>{v || m.name}</Text>
                                <Space size={4} wrap>
                                    {m.gpu && <Tag color="purple">{m.gpu}</Tag>}
                                    {m.preorder && <Tag color="blue">{t('Pre-order')}</Tag>}
                                    {m.stock <= 3 && <Tag color="orange">{t('Only {{n}} left', { n: m.stock })}</Tag>}
                                </Space>
                            </Space>
                        ),
                    },
                    { title: t('Memory'), dataIndex: 'ram_gb', sorter: (a, b) => (a.ram_gb || 0) - (b.ram_gb || 0), render: gb },
                    {
                        title: t('Storage'), dataIndex: 'storage_gb', sorter: (a, b) => (a.storage_gb || 0) - (b.storage_gb || 0),
                        render: (v, m) => <Space direction="vertical" size={0}><Text>{disk(v)} {m.storage_type}</Text><Text type="secondary" style={{ fontSize: 12 }}>{m.storage}</Text></Space>,
                    },
                    ...(hasBw ? [{ title: t('Bandwidth'), dataIndex: 'bandwidth', render: (v) => v || '-' }] : []),
                    { title: t('Location'), dataIndex: 'location', sorter: (a, b) => (a.location || '').localeCompare(b.location || ''), render: (v, m) => <CountryLabel code={m.country}>{t(v || m.data_center)}</CountryLabel> },
                    {
                        title: t('Price'), dataIndex: 'from', defaultSortOrder: 'ascend', sorter: (a, b) => a.from - b.from,
                        render: (v) => <span style={{ whiteSpace: 'nowrap' }}><Text strong>{money(v)}</Text><Text type="secondary">{t('/mo')}</Text></span>,
                    },
                ]} />
        </>
    );
};

const OrderDedicated = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [models, setModels] = useState(null);
    const [available, setAvailable] = useState(true);
    const [sel, setSel] = useState(null);          // selected model id
    const [detail, setDetail] = useState(null);    // model with groups
    const [cycle, setCycle] = useState('monthly');
    const [choices, setChoices] = useState({});
    const [hostname, setHostname] = useState('');
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loadingModel, setLoadingModel] = useState(false);
    const quoteSeq = useRef(0);
    const configRef = useRef(null);

    useEffect(() => {
        (async () => {
            const r = await api.catalog();
            if (!isOk(r)) { message.error(errMsg(r, t('Failed to load'))); setModels([]); return; }
            setAvailable(r.data?.available !== false);
            setModels(arr(r.data?.models));
        })();
    }, [t]);

    const pick = async (m) => {
        setSel(m.id);
        if (window.innerWidth < 1200) setTimeout(() => configRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
        setQuote(null);
        setLoadingModel(true);
        const r = await api.model(m.id);
        setLoadingModel(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Failed to load'))); setDetail(null); return; }
        setDetail(r.data);
        const cycles = Object.keys(r.data?.cycles || {});
        setCycle(cycles.includes('monthly') ? 'monthly' : cycles[0]);
        setChoices(Object.fromEntries(arr(r.data?.groups).map((g) => [g.id, g.default])));
    };

    // ?model=<id> (website landing "Order" button) preselects that server
    const wanted = Number(params.get('model')) || 0;
    useEffect(() => {
        const m = wanted && arr(models).find((x) => x.id === wanted);
        if (m) pick(m);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [models, wanted]);

    // server-side quote on every change (the price shown is always the price charged)
    useEffect(() => {
        if (!detail) return;
        const seq = ++quoteSeq.current;
        const h = setTimeout(async () => {
            const r = await api.quote({ product_id: detail.id, cycle, options: choices });
            if (seq !== quoteSeq.current) return;
            if (isOk(r)) setQuote(r.data);
            else { setQuote(null); message.error(errMsg(r, t('Failed to load'))); }
        }, 250);
        return () => clearTimeout(h);
    }, [detail, cycle, choices, t]);

    const order = async () => {
        if (!detail) return;
        setBusy(true);
        const r = await api.order({ product_id: detail.id, cycle, options: choices, hostname: hostname.trim() });
        setBusy(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Order failed'))); return; }
        navigate(userPath(`/billing/invoices/${r.data.invoice_id}`));
    };

    if (models === null) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

    return (
        <div>
            <PageHeader title={t('Order a dedicated server')} onBack={() => navigate(userPath('/dedicated'))} />
            {!available || !models.length ? (
                <Card><Empty description={available ? t('No servers in stock right now') : t('Dedicated servers are not available right now')} /></Card>
            ) : (
                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={16}>
                        <Card size="small" title={t('Choose a server')}>
                            <ModelTable models={models} sel={sel} onPick={pick} />
                        </Card>
                    </Col>
                    <Col xs={24} xl={8}>
                        <div ref={configRef} /><Card size="small" title={t('Configuration')} style={{ position: 'sticky', top: 16 }}>
                            {loadingModel ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : !detail ? (
                                <Text type="secondary">{t('Pick a server from the list')}</Text>
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                    <div>
                                        <Text strong>{detail.name}</Text>{' '}
                                        <Tag><CountryFlag code={detail.country || models.find((m) => m.id === detail.id)?.country} />{t(detail.location || DC_LABEL[detail.data_center] || detail.data_center)}</Tag>
                                    </div>
                                    <Segmented block value={cycle} onChange={setCycle}
                                        options={CYCLES.filter((c) => detail.cycles?.[c]).map((c) => ({ label: t(CYCLE_LABEL[c]), value: c }))} />
                                    {arr(detail.groups).filter((g) => !g.fixed && arr(g.options).length > 1).map((g) => (
                                        <div key={g.id}>
                                            <div style={{ fontSize: 12, marginBottom: 4 }}>{g.name}</div>
                                            <Select style={{ width: '100%' }} value={choices[g.id]} onChange={(v) => setChoices((c) => ({ ...c, [g.id]: v }))}
                                                options={arr(g.options).map((o) => {
                                                    const p = o.prices?.[cycle]?.price || 0;
                                                    return { value: o.id, label: p > 0 ? `${o.name} (+${money(p)})` : o.name };
                                                })} />
                                        </div>
                                    ))}
                                    <div>
                                        <div style={{ fontSize: 12, marginBottom: 4 }}>{t('Hostname')}</div>
                                        <Input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="server.example.com" maxLength={253} />
                                    </div>
                                    {quote && (
                                        <div>
                                            {arr(quote.lines).map((l) => (
                                                <div key={l.description} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                    <Text type="secondary" ellipsis style={{ maxWidth: '70%' }}>{l.kind === 'setup' ? t('Setup fee') : l.description}</Text>
                                                    <Text>{money(l.amount)}</Text>
                                                </div>
                                            ))}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                                                <Text strong>{t('Due today')}</Text>
                                                <Text strong style={{ fontSize: 18 }}>{money(quote.total)}</Text>
                                            </div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {t('Then {{price}} {{cycle}}', { price: money(quote.recurring), cycle: t(CYCLE_LABEL[cycle]).toLowerCase() })}
                                            </Text>
                                        </div>
                                    )}
                                    <Button type="primary" block size="large" loading={busy} disabled={!quote} onClick={order}>{t('Order now')}</Button>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Your billing period starts when the server is delivered.')}</Text>
                                </Space>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default OrderDedicated;
